import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../auth/services/auth.service';
import { TipoDocumento } from '../models/asistencia.model';
import {
  MedellinJovenCuentasPort,
  ResultadoVerificacionMJ,
  SesionMJ
} from '../ports/medellin-joven-cuentas.port';

interface UsuarioDirectorioMJ {
  id: number;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  second_surname?: string;
  name?: string;
  identification_type_id?: number;
  identification_number?: string | number;
  email?: string;
  phone?: string | number;
  activated_user?: boolean;
}

@Injectable({ providedIn: 'root' })
export class HttpMedellinJovenCuentasAdapter extends MedellinJovenCuentasPort {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiUrl = environment.URL_SERVER.endsWith('/') ? environment.URL_SERVER : `${environment.URL_SERVER}/`;

  private mapearTipoDocumento(typeId?: number): TipoDocumento {
    switch (Number(typeId)) {
      case 2:
        return 'TI';
      case 3:
        return 'CE';
      case 4:
        return 'PPT';
      case 5:
        return 'PASAPORTE';
      default:
        return 'CC';
    }
  }

  private armarNombreCompleto(u?: Partial<UsuarioDirectorioMJ> | null): string {
    if (!u) return '';
    return [u.first_name, u.middle_name, u.name, u.last_name, u.second_surname]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  async verificarCorreo(correo: string, documento?: string): Promise<ResultadoVerificacionMJ> {
    const limpio = correo.trim().toLowerCase();
    const docLimpio = (documento ?? '').trim().replace(/\s+/g, '');
    if (!limpio && !docLimpio) {
      return { estado: 'NO_REGISTRADO', documentoTieneCuenta: false, detalle: 'Correo vacío.' };
    }

    // 1. Verificamos el correo y documento simultáneamente con el endpoint de asistencia MML
    try {
      const resp = await firstValueFrom(
        this.http.post<{
          registrado: boolean;
          documentoTieneCuenta?: boolean;
          mjUserId?: number;
          correoVerificado?: string;
          correoDelDocumento?: string;
          nombreTitular?: string;
          celular?: string;
          tipoDocumento?: TipoDocumento;
          numeroDocumento?: string;
          coincideDocumento?: boolean;
          correoCoincideConDocumento?: boolean;
          correoPerteneceAOtroDocumento?: boolean;
          documentoDelCorreo?: string;
          detalle?: string;
        }>(`${this.apiUrl}mml-asistencia/verificar-correo-mj`, {
          correo: limpio,
          numeroDocumento: docLimpio || undefined
        })
      );
      if (resp) {
        const docTieneCuenta = resp.documentoTieneCuenta ?? Boolean(resp.mjUserId || resp.registrado);
        const correoCoincide = resp.correoCoincideConDocumento ?? (resp.registrado && resp.coincideDocumento !== false);
        const correoDelDoc = resp.correoDelDocumento ?? (docTieneCuenta ? resp.correoVerificado : undefined);
        if (resp.registrado && resp.coincideDocumento !== false) {
          return {
            estado: 'REGISTRADO',
            mjUserId: resp.mjUserId,
            correoMJ: resp.correoVerificado ?? limpio,
            nombreMJ: resp.nombreTitular,
            celularMJ: resp.celular,
            tipoDocumentoMJ: resp.tipoDocumento ?? 'CC',
            documentoMJ: resp.numeroDocumento,
            documentoCoincide: true,
            documentoTieneCuenta: true,
            correoCoincideConDocumento: true,
            correoDelDocumento: correoDelDoc ?? resp.correoVerificado ?? limpio,
            correoPerteneceAOtroDocumento: false,
            detalle: resp.detalle ?? `Cuenta activa en Medellín Joven (${limpio})`
          };
        }
        return {
          estado: 'NO_REGISTRADO',
          mjUserId: docTieneCuenta ? resp.mjUserId : undefined,
          correoMJ: correoDelDoc,
          nombreMJ: resp.nombreTitular,
          celularMJ: resp.celular,
          tipoDocumentoMJ: resp.tipoDocumento,
          documentoMJ: resp.numeroDocumento,
          documentoCoincide: false,
          documentoTieneCuenta: docTieneCuenta,
          correoCoincideConDocumento: correoCoincide,
          correoDelDocumento: correoDelDoc,
          correoPerteneceAOtroDocumento: Boolean(resp.correoPerteneceAOtroDocumento),
          documentoDelCorreo: resp.documentoDelCorreo,
          detalle: resp.detalle ?? 'El correo o documento no coincide en Medellín Joven.'
        };
      }
    } catch {
      // Continúa al respaldo con AuthService
    }

    // 2. Respaldo directo con AuthService si tenemos documento
    if (docLimpio) {
      try {
        const resDoc = await firstValueFrom(this.authService.buscarUsuarioPorDocumento(docLimpio));
        if (resDoc?.success) {
          if (resDoc.existe && resDoc.data) {
            const u: UsuarioDirectorioMJ = resDoc.data;
            const correoOficial = (u.email ?? '').trim().toLowerCase();
            const docOficial = String(u.identification_number ?? docLimpio).trim();
            const nombreMJ = this.armarNombreCompleto(u);
            if (docOficial === docLimpio && (!limpio || correoOficial === limpio)) {
              return {
                estado: 'REGISTRADO',
                mjUserId: Number(u.id),
                correoMJ: correoOficial || limpio,
                nombreMJ: nombreMJ || undefined,
                celularMJ: u.phone ? String(u.phone) : undefined,
                tipoDocumentoMJ: this.mapearTipoDocumento(u.identification_type_id),
                documentoMJ: docOficial,
                documentoCoincide: true,
                documentoTieneCuenta: true,
                correoCoincideConDocumento: true,
                correoDelDocumento: correoOficial || limpio,
                correoPerteneceAOtroDocumento: false,
                detalle: `Cuenta y correo verificados con el documento ${docOficial} en Medellín Joven (${correoOficial})`
              };
            }
            let correoEnOtro = false;
            if (limpio && limpio !== correoOficial) {
              try {
                correoEnOtro = Boolean(await firstValueFrom(this.authService.checkEmailExists(limpio)));
              } catch {
                // ignorar error secundario
              }
            }
            return {
              estado: 'NO_REGISTRADO',
              mjUserId: Number(u.id),
              correoMJ: correoOficial || undefined,
              nombreMJ: nombreMJ || undefined,
              celularMJ: u.phone ? String(u.phone) : undefined,
              documentoMJ: docOficial,
              documentoCoincide: false,
              documentoTieneCuenta: true,
              correoCoincideConDocumento: false,
              correoDelDocumento: correoOficial || undefined,
              correoPerteneceAOtroDocumento: correoEnOtro,
              detalle: correoEnOtro
                ? `El documento ${docLimpio} sí tiene cuenta en MJ con el correo ${correoOficial}; además, ${limpio} pertenece a otro documento.`
                : `El documento ${docLimpio} tiene cuenta en Medellín Joven con otro correo (${correoOficial}), no con ${limpio}.`
            };
          } else {
            // El documento no tiene cuenta en Medellín Joven; comprobamos si el correo pertenece a otro documento
            let correoEnOtro = false;
            if (limpio) {
              try {
                correoEnOtro = Boolean(await firstValueFrom(this.authService.checkEmailExists(limpio)));
              } catch {
                // ignorar error secundario
              }
            }
            return {
              estado: 'NO_REGISTRADO',
              documentoCoincide: false,
              documentoTieneCuenta: false,
              correoCoincideConDocumento: false,
              correoPerteneceAOtroDocumento: correoEnOtro,
              detalle: correoEnOtro
                ? `El documento ${docLimpio} no tiene cuenta en Medellín Joven, pero el correo ${limpio} pertenece a otro número de documento en MJ.`
                : `El documento ${docLimpio} no tiene cuenta registrada en Medellín Joven.`
            };
          }
        }
      } catch {
        return {
          estado: 'ERROR_VERIFICACION',
          detalle: 'No fue posible verificar la correspondencia entre correo y documento en Medellín Joven.'
        };
      }
    }

    try {
      const existe = await firstValueFrom(this.authService.checkEmailExists(limpio));
      if (existe) {
        return {
          estado: 'REGISTRADO',
          correoMJ: limpio,
          documentoCoincide: true,
          documentoTieneCuenta: true,
          correoCoincideConDocumento: true,
          correoDelDocumento: limpio,
          detalle: `Correo registrado en Medellín Joven (${limpio}).`
        };
      }
      return {
        estado: 'NO_REGISTRADO',
        documentoTieneCuenta: false,
        correoCoincideConDocumento: false,
        correoPerteneceAOtroDocumento: false,
        detalle: 'No existe cuenta en Medellín Joven con ese correo.'
      };
    } catch {
      return {
        estado: 'ERROR_VERIFICACION',
        detalle: 'No fue posible consultar el servicio de cuentas de Medellín Joven en este momento.'
      };
    }
  }

  async verificarDocumento(documento: string): Promise<ResultadoVerificacionMJ> {
    const limpio = documento.trim().replace(/\s+/g, '');
    if (!limpio) {
      return { estado: 'NO_REGISTRADO', detalle: 'Documento vacío.' };
    }

    // 1. Usar directamente el método oficial que ya existe en Medellín Joven: AuthService.buscarUsuarioPorDocumento (/buscar-usuario-por-documento)
    try {
      const busqueda = await firstValueFrom(this.authService.buscarUsuarioPorDocumento(limpio));
      if (busqueda && busqueda.success) {
        if (busqueda.existe && busqueda.data) {
          const u: UsuarioDirectorioMJ = busqueda.data;
          const nombreMJ = this.armarNombreCompleto(u);
          return {
            estado: 'REGISTRADO',
            mjUserId: Number(u.id),
            correoMJ: u.email ? String(u.email).trim().toLowerCase() : undefined,
            nombreMJ: nombreMJ || undefined,
            celularMJ: u.phone ? String(u.phone) : undefined,
            tipoDocumentoMJ: this.mapearTipoDocumento(u.identification_type_id),
            documentoMJ: u.identification_number ? String(u.identification_number) : limpio,
            documentoCoincide: true,
            detalle: 'Pertenece a Medellín Joven (cuenta activa verificada).'
          };
        }
        return {
          estado: 'NO_REGISTRADO',
          detalle: 'Documento sin cuenta activa en Medellín Joven.'
        };
      }
    } catch {
      // Si falla la petición a /buscar-usuario-por-documento, continúa con el respaldo
    }

    // 2. Respaldo con /mml-asistencia/verificar-documento-mj y AuthService.checkDocumentExists (/validar-documento)
    try {
      const resp = await firstValueFrom(
        this.http.post<{
          estado: 'REGISTRADO' | 'NO_REGISTRADO';
          mjUserId?: number;
          correoVerificado?: string;
          nombreCompleto?: string;
          celular?: string;
          tipoDocumento?: TipoDocumento;
          numeroDocumento?: string;
          detalle?: string;
        }>(`${this.apiUrl}mml-asistencia/verificar-documento-mj`, {
          numeroDocumento: limpio
        })
      );
      if (resp && resp.estado === 'REGISTRADO') {
        return {
          estado: 'REGISTRADO',
          mjUserId: resp.mjUserId,
          correoMJ: resp.correoVerificado,
          nombreMJ: resp.nombreCompleto,
          celularMJ: resp.celular,
          tipoDocumentoMJ: resp.tipoDocumento,
          documentoMJ: resp.numeroDocumento,
          documentoCoincide: true,
          detalle: resp.detalle
        };
      }
      return {
        estado: 'NO_REGISTRADO',
        detalle: resp?.detalle ?? 'Documento sin cuenta activa en Medellín Joven.'
      };
    } catch {
      try {
        const existe = await firstValueFrom(this.authService.checkDocumentExists(limpio));
        if (existe) {
          return {
            estado: 'REGISTRADO',
            documentoMJ: limpio,
            documentoCoincide: true,
            detalle: 'Documento registrado en Medellín Joven.'
          };
        }
        return {
          estado: 'NO_REGISTRADO',
          detalle: 'Documento no registrado en Medellín Joven.'
        };
      } catch {
        return {
          estado: 'ERROR_VERIFICACION',
          detalle: 'No fue posible verificar el documento en Medellín Joven en este momento.'
        };
      }
    }
  }

  async buscarUsuariosDirectorio(termino: string): Promise<ResultadoVerificacionMJ[]> {
    const q = (termino ?? '').trim();

    const mapa = new Map<number, ResultadoVerificacionMJ>();
    const agregar = (r?: ResultadoVerificacionMJ | null) => {
      if (r && r.estado === 'REGISTRADO' && r.mjUserId) {
        mapa.set(Number(r.mjUserId), r);
      }
    };

    try {
      const resp = await firstValueFrom(
        this.http.post<{
          resultados?: Array<{
            mjUserId: number;
            tipoDocumento?: TipoDocumento;
            numeroDocumento?: string;
            nombreCompleto?: string;
            correoVerificado?: string;
            celular?: string;
          }>;
        }>(`${this.apiUrl}mml-asistencia/buscar-usuarios-mj`, { termino: q })
      );
      if (resp && Array.isArray(resp.resultados)) {
        for (const u of resp.resultados) {
          if (u && u.mjUserId) {
            agregar({
              estado: 'REGISTRADO',
              mjUserId: Number(u.mjUserId),
              tipoDocumentoMJ: u.tipoDocumento ?? 'CC',
              documentoMJ: u.numeroDocumento ?? '',
              nombreMJ: u.nombreCompleto ?? 'Usuario de Medellín Joven',
              correoMJ: u.correoVerificado ?? '',
              celularMJ: u.celular ?? '',
              documentoCoincide: true
            });
          }
        }
      }
    } catch {
      // Si el endpoint aún no responde, continuamos con los respaldos por documento y correo
    }

    if (q.includes('@')) {
      const porCorreo = await this.verificarCorreo(q);
      agregar(porCorreo);
    }

    const soloDigitos = q.replace(/\D/g, '');
    if (soloDigitos.length >= 4) {
      const porDoc = await this.verificarDocumento(soloDigitos);
      agregar(porDoc);
    }

    return Array.from(mapa.values());
  }

  async registrarCuenta(datos: {
    nombre: string;
    correo: string;
    documento: string;
    password?: string;
  }): Promise<ResultadoVerificacionMJ> {
    return this.verificarCorreo(datos.correo, datos.documento);
  }

  async iniciarSesion(correo: string, password: string): Promise<SesionMJ | null> {
    try {
      const resp: any = await firstValueFrom(
        this.authService.login({
          correo: correo.trim().toLowerCase(),
          clave: password
        })
      );
      if (resp?.codigo === 2 && resp?.data?.data?.token) {
        this.authService.accessToken = resp.data.data.token;
        this.authService.setUser(resp.data.data);
      } else if (resp?.success && resp?.data) {
        const token = typeof resp.data === 'string' ? resp.data : resp.data.token;
        if (token) {
          this.authService.accessToken = token;
          await firstValueFrom(this.authService.validateAuth());
        }
      } else {
        return null;
      }
      return this.sesionActual();
    } catch {
      return null;
    }
  }

  sesionActual(): SesionMJ | null {
    const token = this.authService.getToken();
    if (!token) return null;
    let u = this.authService.getUser();
    if (!u || !u.id) {
      try {
        const decoded: any = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);
        if (decoded && decoded.id && (!decoded.exp || decoded.exp > now)) {
          this.authService.setUser(decoded);
          u = decoded;
        } else {
          return null;
        }
      } catch {
        return null;
      }
    }
    if (!u || !u.id) return null;
    const nombre = this.armarNombreCompleto(u) || u.email || 'Usuario Medellín Joven';
    return {
      mjUserId: Number(u.id),
      nombre,
      correo: (u.email ?? '').trim().toLowerCase(),
      documento: u.identification_number ? String(u.identification_number).trim() : undefined,
      token,
      iniciadaEn: new Date().toISOString()
    };
  }

  cerrarSesion(): void {
    localStorage.removeItem('accessToken');
    this.authService.setUser(null);
  }
}
